import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { setModalContent } from 'src/redux/modules/modalEvent';
import DeleteEventModal from '../calendar/DeleteEventModals/DeleteEventModal';
import {
  convertStartDateTimeToString,
  ConvertToLocalDate,
  ConvertToLocalTime,
  getEndTime,
} from '../../utils';
import EventDetailTooltip from './EventDetailTooltip';
import { Appointment } from '../calendar/calendarTypes';
import { Typography } from 'src/latest/src/components';

interface Prop {
  clientName?: string;
  officeLocation: string;
  onDelete: (appointmentId: number) => void;
  recurrent: boolean;
  eventId: string;
  eventType?: string;
  schedulerName?: string;
  therapistName?: string;
  startDateTime?: any;
  duration: number;
  activeTooltipId?: string;
  onToggleTooltip: (eventId: string) => void;
  appointment: Appointment;
}

const AppointmentDetails: React.FC<Prop> = ({
  duration,
  startDateTime,
  onDelete,
  recurrent,
  eventId,
  eventType,
  activeTooltipId,
  onToggleTooltip,
  appointment,
  clientName,
}) => {
  const [finalDateTime, setFinalDateTime] = useState<any>();
  const ref = useRef(null);
  const dispatch = useDispatch();

  const isTooltipVisible = activeTooltipId === eventId;

  const toggleTooltip = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onToggleTooltip(eventId);
  };

  const closeTooltip = () => {
    if (isTooltipVisible) {
      onToggleTooltip(eventId);
    }
  };

  useEffect(() => {
    setFinalDateTime(renderDateTimeString());
  }, []);

  const renderDateTimeString = () => {
    const formattedStartDateTime: string =
      convertStartDateTimeToString(startDateTime);
    const formattedEndDateTime: Date = getEndTime(startDateTime, duration);
    const dateString = ConvertToLocalDate(formattedStartDateTime);
    const startTimeString = ConvertToLocalTime(formattedStartDateTime);
    const endTimeString = ConvertToLocalTime(formattedEndDateTime.toString());

    return `${dateString}  ${startTimeString} - ${endTimeString}`;
  };

  const handleDelete = () => {
    dispatch(
      setModalContent({
        modalContent: (
          <DeleteEventModal
            eventId={eventId}
            isRecurrent={recurrent}
            getLatestSchedules={onDelete}
            eventType={eventType}
            eventData={appointment}
          />
        ),
        isWithCancelButton: false,
        size: 'lg',
        title: recurrent
          ? `Delete Recurring Appointment`
          : `Delete Appointment`,
        className: recurrent
          ? 'delete-recurring'
          : 'delete-non-recurring-modal',
      }),
    );
  };

  return (
    <div
      ref={ref}
      onClick={toggleTooltip}
      style={{
        cursor: 'pointer',
        height: '100%',
        width: '100%',
      }}
    >
      <div className="d-flex justify-content-center align-items-center">
        <Typography
          variant="size-12"
          weight="bold"
          color="color-28"
          className="text-uppercase"
        >
          Details
        </Typography>
        <FontAwesomeIcon size="xs" className="arrowright" icon={faArrowRight} />
      </div>

      <EventDetailTooltip
        data={appointment}
        finalDateTime={finalDateTime}
        handleDelete={handleDelete}
        isCalendarTooltip={false}
        isAppointmentTooltip={true}
        handleEdit={undefined}
        isTooltipVisible={isTooltipVisible}
        tooltipTarget={ref}
        showTooltip={undefined}
        closeTooltip={closeTooltip}
        clientName={clientName}
      />
    </div>
  );
};

export default AppointmentDetails;
